import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

/**
 * Route table.
 *
 * Feature screens are lazily loaded so the initial bundle is the shell plus
 * the one screen being visited. The measured target is a first paint under a
 * second on a cold cache, and shipping the billing module to a parent who
 * only ever opens the schedule is the easiest way to miss it.
 *
 * `AppShell` owns the persistent chrome — sidebar, top bar, command palette,
 * the global keyboard map — so navigating between screens never remounts it.
 */
const AppShell = lazy(() => import("./AppShell").then((m) => ({ default: m.AppShell })));
const SignIn = lazy(() => import("@/features/auth/SignIn").then((m) => ({ default: m.SignIn })));
const SignUp = lazy(() => import("@/features/auth/SignUp").then((m) => ({ default: m.SignUp })));
const ForgotPassword = lazy(() =>
  import("@/features/auth/ForgotPassword").then((m) => ({ default: m.ForgotPassword })),
);
const ResetPassword = lazy(() =>
  import("@/features/auth/ResetPassword").then((m) => ({ default: m.ResetPassword })),
);
const VerifyEmail = lazy(() =>
  import("@/features/auth/VerifyEmail").then((m) => ({ default: m.VerifyEmail })),
);
const ConfirmEmailChange = lazy(() =>
  import("@/features/auth/ConfirmEmailChange").then((m) => ({ default: m.ConfirmEmailChange })),
);
/**
 * Development-only harnesses.
 *
 * `import.meta.env.DEV` is a build-time constant, so in a production build this
 * resolves to `false`, the branch below is dropped and the lazy import is
 * tree-shaken out entirely. Before this guard existed the focus gallery was
 * shipping in the production bundle — a test surface on a live product, one
 * URL away from anyone who guessed it.
 */
const FocusGallery = import.meta.env.DEV
  ? lazy(() => import("@/features/gallery/FocusGallery").then((m) => ({ default: m.FocusGallery })))
  : null;

export function App() {
  return (
    <Suspense fallback={<BootSplash />}>
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        {/*
         * The credential routes. All four are OUTSIDE `AppShell` because every
         * one of them is reached by somebody who is not signed in — a reset
         * link opened in a browser that has never seen this product, a
         * confirmation followed on a phone. Mounted under the shell they would
         * hit its signed-out redirect and bounce to `/signin`, which is
         * precisely the bug `/reset` had: `SignIn` linked to it, no route
         * matched, the catch-all sent it to the shell, and the reader was
         * returned to the page they had just left.
         *
         * `/reset` and `/reset/:token` are two screens, not one with a branch:
         * asking for a link and choosing a password share no state and no
         * failure modes.
         */}
        <Route path="/reset" element={<ForgotPassword />} />
        <Route path="/reset/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/email/change/:token" element={<ConfirmEmailChange />} />
        {FocusGallery && <Route path="/__focus" element={<FocusGallery />} />}
        <Route path="/*" element={<AppShell />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Deliberately not a spinner. A spinner on first paint reads as slow even when
 * it is fast; an empty canvas in the right colour reads as instant, and the
 * shell arrives before a spinner would have finished one rotation.
 */
function BootSplash() {
  return <div style={{ minHeight: "100dvh", background: "var(--canvas)" }} aria-hidden="true" />;
}
